import React, {
	useState,
	useRef,
	type KeyboardEvent,
	type ChangeEvent,
	type FocusEvent,
	type ReactNode,
	useCallback,
	useLayoutEffect,
	useMemo,
	useEffect,
} from 'react';
import {
	DatePicker as AkDatePicker,
	type DateTimePickerSelectProps,
} from '@atlaskit/datetime-picker';
import { layers } from '@atlaskit/theme/constants';
import { token } from '@atlaskit/tokens';
import { standardizeLocale } from '@atlassian/jira-common-constants/src/supported-locales.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { useIntl } from '@atlassian/jira-intl';
import { getLocale } from '@atlassian/jira-platform-utils-date-fns/src/main.tsx';
import { useLocale } from '@atlassian/jira-tenant-context-controller/src/components/locale/index.tsx';
import { formatDateWithIntl } from '../date-manipulation/format.tsx';
import { injectMouseOverStyles } from '../select/index.tsx';
import type { Props } from './types.tsx';

export const DatePicker = ({
	isTransparentBackground = true,
	isDisabled = false,
	isActiveOnFirstRender = false,
	showClearIndicator = false,
	onToggle,
	onMenuOpen,
	onChange,
	onFocus,
	onBlur,
	styles,
	placeholder,
	formatOption,
	shouldRenderToParent = false,
	isOpen: isOpenExternal = false,
	...props
}: Props) => {
	const [isOpen, setIsOpen] = useState(isOpenExternal);
	const [isTriggeredByMouseEvent, setIsTriggeredByMouseEvent] = useState(true);
	const calendarRef = useRef<HTMLDivElement>(null);
	const datePickerRef = useRef<HTMLDivElement>(null);
	const intl = useIntl();
	const locale = useLocale();
	const weekStartDay = getLocale(locale)?.options?.weekStartsOn || 0;

	const focusOnCalendar = useCallback(() => {
		const calendarButton = calendarRef?.current?.querySelector('button');
		if (calendarButton) {
			calendarButton.focus();
		}
	}, []);

	useEffect(() => {
		if (isOpen) {
			onMenuOpen && onMenuOpen(() => calendarRef);
			onToggle && onToggle(true);
		} else {
			setIsTriggeredByMouseEvent(true);
			onToggle && onToggle(false);
		}
	}, [isOpen, onMenuOpen, onToggle]);

	useLayoutEffect(() => {
		if (isOpen && !isTriggeredByMouseEvent) {
			setTimeout(() => {
				focusOnCalendar();
			}, 0);
		}
	}, [isOpen, isTriggeredByMouseEvent, focusOnCalendar]);

	const focusOnDatePicker = useCallback(() => {
		const innerCombobox: HTMLInputElement | undefined | null =
			datePickerRef?.current?.querySelector('[role="combobox"]');
		innerCombobox?.focus();
	}, []);

	const onChangeCallback = useCallback(
		(value: string) => {
			setIsOpen(false);
			onChange?.(value);
		},
		[onChange],
	);

	const handleTextInput = useCallback(
		(e: ChangeEvent<HTMLInputElement>) => {
			const inputValue = e.target.value;
			if (!isOpen && inputValue) {
				setIsOpen(true);
			}
		},
		[isOpen],
	);

	const handleKeyDown = useCallback(
		(e: KeyboardEvent<HTMLElement>) => {
			if (isOpen && e.key === 'Escape') {
				e.stopPropagation();
				setIsOpen(false);
				focusOnDatePicker();
			}

			if (
				e.target instanceof Node &&
				!calendarRef?.current?.contains(e.target) &&
				e.type === 'keydown'
			) {
				if (e.key === 'Tab' && isOpen) {
					// when clicking open with mouse and then tabbing to enter calendar
					e.preventDefault();
					focusOnCalendar();
				}
				if (!((e.key === 'Tab' || e.key === 'Shift') && !isOpen)) {
					setIsTriggeredByMouseEvent(false);
					if (e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowUp' || e.key === 'ArrowDown') {
						e.stopPropagation();
						setIsOpen(true);
					}
				}
			}
		},
		[isOpen, focusOnDatePicker, focusOnCalendar],
	);

	const handleKeyUp = useCallback((e: KeyboardEvent) => {
		if (e.key === 'Escape') {
			e.stopPropagation();
		}
	}, []);

	const handleBlur = useCallback(
		(e: FocusEvent<HTMLElement>) => {
			const newlyFocusedElement = e.relatedTarget;
			const target = e.target;
			if (!calendarRef?.current?.contains(newlyFocusedElement)) {
				setIsOpen(false);
			}
			// when calendar closes
			if (!calendarRef?.current?.contains(target)) {
				onBlur && onBlur(e);
			}
		},
		[onBlur],
	);

	const handleMenuOpen = useCallback(() => {
		setIsOpen(true);
	}, []);

	const handleCalendarKeyDown = useCallback(
		(e: KeyboardEvent<HTMLElement>) => {
			let isDayCell = false;
			if (e.target && e.target instanceof HTMLElement) {
				isDayCell = e.target.parentElement?.getAttribute('role') === 'gridcell';
			}
			const isTabKeyDownOnDate = e.key === 'Tab' && !e.shiftKey && e.target && isDayCell;

			// this prevents tabbing onto the browser search bar when tabbing out of the calendar
			if (isTabKeyDownOnDate) {
				e.preventDefault();
				setIsOpen(false);
				focusOnDatePicker();
			}

			if ((e.key === 'Enter' && !isDayCell) || e.key === 'Backspace' || e.key === 'Delete') {
				setIsOpen(false);
				focusOnDatePicker();
			}
		},
		[focusOnDatePicker],
	);

	const selectProps: DateTimePickerSelectProps = useMemo(
		() => ({
			// This helps to avoid expensive built-in AutosizeInput on the first render as we anyway
			// re-render after Select is mounted and its components get available for extension.
			isSearchable: isActiveOnFirstRender,
			styles: {
				...styles,
				// eslint-disable-next-line @typescript-eslint/no-shadow
				menuPortal: (styles: object) => ({
					...styles,
					...(!fg('plan-timeline-non-transposed')
						? {
								zIndex: layers.modal(),
								marginTop: `calc(${token('space.negative.100')} - ${token('space.400')})`,
							}
						: {}),
				}),
				// eslint-disable-next-line @typescript-eslint/no-shadow
				control: (styles: object, state: { isFocused: boolean }) =>
					injectMouseOverStyles(styles, true, state.isFocused, !!isTransparentBackground),
			},
			onMenuOpen: handleMenuOpen,
			onKeyDown: handleKeyDown,
			...(fg('plan-timeline-non-transposed') || shouldRenderToParent
				? {}
				: { menuPortalTarget: globalThis.document?.body }),
		}),
		[
			handleKeyDown,
			handleMenuOpen,
			isActiveOnFirstRender,
			isTransparentBackground,
			shouldRenderToParent,
			styles,
		],
	);

	const appearance = isTransparentBackground ? 'subtle' : 'default';
	// Empty string evaluates as false inside AtlasKit, and makes the datepicker show 2/18/1983 as the placeholder string.
	// In order to avoid this, we turn any empty strings into ' '.
	const placeholderString = isDisabled || placeholder === '' ? ' ' : placeholder;

	const menuInnerWrapper = useCallback(
		({ children }: { children: ReactNode }) => {
			return (
				<div ref={calendarRef} onKeyDown={handleCalendarKeyDown} role="presentation">
					{children}
				</div>
			);
		},
		[handleCalendarKeyDown],
	);

	return (
		<div ref={datePickerRef} role="presentation" onKeyUp={handleKeyUp} onInput={handleTextInput}>
			{/* eslint-disable-next-line @typescript-eslint/ban-ts-comment */}
			{/* @ts-ignore Temporary incompatible selectProps type ('as any' workaround) until @atlaskit/select is upgraded */}
			<AkDatePicker
				{...props}
				isOpen={isOpen}
				hideIcon={props.hideIcon || !showClearIndicator}
				isDisabled={isDisabled}
				onFocus={onFocus}
				onBlur={handleBlur}
				appearance={appearance}
				onChange={onChangeCallback}
				selectProps={selectProps}
				placeholder={placeholderString}
				weekStartDay={weekStartDay ?? 0}
				menuInnerWrapper={menuInnerWrapper}
				locale={locale && standardizeLocale(locale)}
				formatDisplayLabel={
					intl ? (date: string) => formatDateWithIntl(intl, date, formatOption) : undefined
				}
			/>
		</div>
	);
};

export default DatePicker;
